import { MAX_DEPTH, isPlainObject } from './inputUtils';

const RESOURCE_PREFIXES = [
  'assignmentTables',
  'entities',
  'factTables',
  'flags',
  'metrics',
  'surfaces',
  'identities',
  'collections',
] as const;

type ResourceType = (typeof RESOURCE_PREFIXES)[number];

export const SUPPLEMENTAL_RESOURCE_TYPES = [
  'identities',
  'collections',
] as const;

const RESOURCE_NAME_PATTERN = new RegExp(
  `^(${RESOURCE_PREFIXES.join('|')})/[^/\\s]+$`,
);

export const isResourceName = (value: string): boolean =>
  RESOURCE_NAME_PATTERN.test(value);

export const getResourceType = (name: string): ResourceType =>
  name.split('/')[0] as ResourceType;

export type GroupedResourceNames = Record<ResourceType, string[]>;

const emptyGroup = (): GroupedResourceNames => ({
  assignmentTables: [],
  entities: [],
  factTables: [],
  flags: [],
  metrics: [],
  surfaces: [],
  identities: [],
  collections: [],
});

export const collectResourceNames = (input: unknown): GroupedResourceNames => {
  const result = emptyGroup();
  const seen = new Set<string>();

  const walk = (value: unknown, depth: number) => {
    if (depth > MAX_DEPTH) return;

    if (
      typeof value === 'string' &&
      isResourceName(value) &&
      !seen.has(value)
    ) {
      seen.add(value);
      result[getResourceType(value)].push(value);
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        walk(item, depth + 1);
      }
      return;
    }

    if (isPlainObject(value)) {
      for (const v of Object.values(value)) {
        walk(v, depth + 1);
      }
    }
  };

  walk(input, 0);
  return result;
};

export const hasAnyResourceNames = (grouped: GroupedResourceNames): boolean =>
  RESOURCE_PREFIXES.some(prefix => grouped[prefix].length > 0);
